import {
  Button,
  CurrencyInput,
  Drawer,
  Input,
  Label,
  Steps,
} from "@components";
import styles from "@styles/containers/cdp/customer-fidelity.module.scss";
import classNames from "classnames/bind";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import InternalBenefitAPI from "providers/InternalBenefitAPI";
import { useEffect, useState } from "react";
import { If } from "shared/components/control-statements";
import { SuccessIcon } from "shared/components/Icons";
import { NumberInput } from "shared/components/NumberInput";
import { Currency } from "shared/utils/Currency";
const css = classNames.bind(styles);

dayjs.extend(isSameOrAfter);

const Operations = {
  ADD_CASHBACK: "ADD_CASHBACK",
  REMOVE_CASHBACK: "REMOVE_CASHBACK",
};

export default function EditCashback({
  customer,
  brand,
  visible,
  onClose,
  programByPoint,
}) {
  const [operation, setOperation] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [cashback, setCashback] = useState({
    value: null,
    expiration: null,
  });
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadBalance = async () => {
    const internalBenefitAPI = new InternalBenefitAPI();
    const balance = await internalBenefitAPI.getBalanceByCustomerId({
      customerId: customer.id,
      brandId: brand.id,
    });

    setBalance(balance);
  };

  useEffect(() => {
    if (visible) {
      setCurrentStep(1);
      setOperation(null);
      setCashback({
        value: null,
        expiration: null,
      });
      loadBalance();
    }
  }, [visible]);

  const onConfirm = async () => {
    setLoading(true);

    const internalBenefitAPI = new InternalBenefitAPI();
    let errors = [];

    if (operation === Operations.ADD_CASHBACK) {
      const expiresAt = dayjs(cashback.expiration, "DD/MM/YYYY", "br", true);
      const response = await internalBenefitAPI.addCashback({
        customerId: customer.id,
        brandId: brand.id,
        expiresAt: expiresAt.format("YYYY-MM-DD"),
        value: cashback.value,
      });

      errors = response.getErrors([]);
    }

    if (operation === Operations.REMOVE_CASHBACK) {
      const response = await internalBenefitAPI.removeCashback({
        customerId: customer.id,
        brandId: brand.id,
        value: cashback.value,
      });

      errors = response.getErrors([]);
    }

    if (errors.length > 0) {
      setLoading(false);
      return alert({ text: errors[0], buttonText: "Ok" });
    }

    setLoading(false);
    setCurrentStep(4);
  };

  const getTitle = () => {
    switch (currentStep) {
      case 1:
        return "Alterar saldo";
      case 2:
      case 4:
        return operation === Operations.ADD_CASHBACK
          ? "Adicionar valor no saldo"
          : "Remover valor do saldo";
      case 3:
        return "Confirmar valor do saldo";
      default:
        return "";
    }
  };

  const isInvalid = () => {
    const isDateValid = () => {
      const expiration = dayjs(cashback.expiration, "DD/MM/YYYY", "br", true);
      const today = dayjs();
      return expiration.isValid() && expiration.isAfter(today, "day");
    };

    if (operation === Operations.ADD_CASHBACK)
      return Number(cashback.value) > 0 && isDateValid();
    else if (operation === Operations.REMOVE_CASHBACK)
      return Number(cashback.value) > 0 && Number(cashback.value) <= balance;
  };

  return (
    <Drawer
      visible={visible}
      onClose={onClose}
      withoutScroll
      withoutTransition
      title={getTitle()}
      subtitle={currentStep === 4 ? "" : customer?.name || ""}
      showBackButton={currentStep !== 4}
      backButton={() =>
        currentStep === 1 ? onClose() : setCurrentStep(currentStep - 1)
      }
    >
      <If cond={currentStep !== 4}>
        <Steps steps={3} currentStep={currentStep} />
      </If>
      <div className={styles.fidelityContainer}>
        <If cond={currentStep === 1}>
          <div className={styles.balance}>
            <div className="d-flex flex-column w-100">
              <div className="d-flex align-center justify-between">
                <p className={styles.label}>SALDO ATUAL</p>
                <p className={styles.total}>
                  {programByPoint
                    ? Currency.formatCurrency(balance)
                    : `${balance} pontos`}
                </p>
              </div>
              <div className="d-flex gap-15 mt-20">
                <div
                  className={css({
                    add: true,
                    selected: operation === Operations.ADD_CASHBACK,
                  })}
                  onClick={() => setOperation(Operations.ADD_CASHBACK)}
                >
                  <i className="fal fa-pen-to-square" />
                  <span>
                    Adicionar {programByPoint ? "cashback" : "pontos"}
                  </span>
                </div>
                <div
                  className={css({
                    remove: true,
                    selected: operation === Operations.REMOVE_CASHBACK,
                  })}
                  onClick={() => setOperation(Operations.REMOVE_CASHBACK)}
                >
                  <i className="fal fa-pen-to-square" />
                  <span>
                    Remover {programByPoint ? "cashback" : "pontos"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </If>

        <If cond={currentStep === 2}>
          <div className={styles.balance}>
            <div className="d-flex align-center justify-between w-100">
              <p className={styles.label}>SALDO ATUAL</p>
              <p className={styles.total}>
                {programByPoint
                  ? Currency.formatCurrency(balance)
                  : `${balance} pontos`}
              </p>
            </div>
          </div>

          <If cond={operation === Operations.ADD_CASHBACK}>
            {programByPoint && (
              <Label>
                Insira o valor que deseja adicionar no saldo do cliente
              </Label>
            )}
            {!programByPoint && (
              <Label>
                Insira os pontos que deseja adicionar no saldo do cliente
              </Label>
            )}
            <If cond={programByPoint}>
              <CurrencyInput
                placeholder="R$ 0,00"
                value={cashback.value}
                onChange={(e, m, f) => {
                  setCashback({
                    ...cashback,
                    value: f,
                  });
                }}
                className="mb-15"
              />
            </If>
            <If cond={!programByPoint}>
              <NumberInput
                placeholder="0 pontos"
                suffix={" pontos"}
                value={cashback.value}
                onChange={(e, m, value) => {
                  setCashback({
                    ...cashback,
                    value,
                  });
                }}
                className="mb-15"
              />
            </If>

            <If cond={programByPoint}>
              <Label>Insira a data de expiração do valor</Label>
            </If>
            <If cond={!programByPoint}>
              <Label>Insira a data de expiração dos pontos</Label>
            </If>
            <Input
              placeholder="dd/mm/aaaa"
              mask="99/99/9999"
              type="tel"
              value={cashback.expiration}
              onChange={(e) =>
                setCashback({ ...cashback, expiration: e.target.value })
              }
            />
          </If>

          <If cond={operation === Operations.REMOVE_CASHBACK}>
            <If cond={programByPoint}>
              <Label>
                Insira o valor que deseja remover do saldo do cliente
              </Label>
              <CurrencyInput
                placeholder="R$ 0,00"
                value={cashback.value}
                onChange={(e, m, f) => {
                  setCashback({
                    ...cashback,
                    value: f,
                  });
                }}
              />
            </If>
            <If cond={!programByPoint}>
              <Label>
                Insira os pontos que deseja remover do saldo do cliente
              </Label>
              <NumberInput
                placeholder="0 pontos"
                suffix={" pontos"}
                value={cashback.value}
                onChange={(e, m, value) => {
                  setCashback({
                    ...cashback,
                    value,
                  });
                }}
              />
            </If>
          </If>
        </If>

        <If cond={currentStep === 3}>
          <div className={styles.oldBalance}>
            <div className="d-flex align-center justify-between w-100">
              <p className={styles.label}>SALDO ATUAL</p>
              <p className={styles.total}>
                {programByPoint
                  ? Currency.formatCurrency(balance)
                  : `${balance} pontos`}
              </p>
            </div>
          </div>
          <div className={styles.newBalance}>
            <div className="d-flex align-center justify-between w-100">
              <div className="d-flex flex-column align-start">
                <p className={styles.label}>NOVO SALDO</p>
                <p
                  className={css({
                    value: true,
                    positive: operation === Operations.ADD_CASHBACK,
                    negative: operation === Operations.REMOVE_CASHBACK,
                  })}
                >
                  <If cond={operation === Operations.ADD_CASHBACK}>
                    <If cond={programByPoint}>
                      {`+ ${Currency.formatCurrency(cashback.value)}`}
                    </If>
                    <If cond={!programByPoint}>
                      {`+ ${cashback.value} pontos`}
                    </If>
                  </If>
                  <If cond={operation === Operations.REMOVE_CASHBACK}>
                    <If cond={programByPoint}>
                      {`- ${Currency.formatCurrency(cashback.value)}`}
                    </If>
                    <If cond={!programByPoint}>
                      {`- ${cashback.value} pontos`}
                    </If>
                  </If>
                </p>
              </div>
              <p className={styles.total}>
                <If cond={operation === Operations.ADD_CASHBACK}>
                  <If cond={programByPoint}>
                    {Currency.formatCurrency(balance + cashback.value)}
                  </If>
                  <If cond={!programByPoint}>
                    {balance + cashback.value} pontos
                  </If>
                </If>
                <If cond={operation === Operations.REMOVE_CASHBACK}>
                  <If cond={programByPoint}>
                    {Currency.formatCurrency(balance - cashback.value)}
                  </If>
                  <If cond={!programByPoint}>
                    {balance - cashback.value} pontos
                  </If>
                </If>
              </p>
            </div>
          </div>
        </If>

        <If cond={currentStep === 4}>
          <div className={styles.success}>
            <div className={styles.successIcon}>
              <SuccessIcon color="#1f1f1f" size={14} />
            </div>
            <p>
              O saldo de {programByPoint ? "cashback" : "pontos"} do cliente
              foi alterado com sucesso.
            </p>
          </div>
        </If>
      </div>

      <If cond={currentStep === 1}>
        <div className={styles.footer}>
          <Button
            design="primary"
            className="w-100"
            disabled={!operation}
            onClick={() => setCurrentStep(currentStep + 1)}
          >
            Continuar
          </Button>
        </div>
      </If>

      <If cond={!!operation && currentStep === 2}>
        <div className={styles.footer}>
          <Button
            design="primary"
            className="w-100"
            onClick={() => setCurrentStep(currentStep + 1)}
            disabled={!isInvalid()}
          >
            {operation === Operations.ADD_CASHBACK
              ? `Adicionar ${programByPoint ? "este valor" : "estes pontos"}`
              : `Remover ${programByPoint ? "este valor" : "estes pontos"}`}
          </Button>
        </div>
      </If>

      <If cond={!!operation && currentStep === 3}>
        <div className={styles.footer}>
          <Button
            design="primary"
            className="w-100"
            onClick={onConfirm}
            loading={loading}
            disabled={loading}
          >
            Confirmar alteração de saldo
          </Button>
        </div>
      </If>

      <If cond={!!operation && currentStep === 4}>
        <div className={styles.footer}>
          <Button className="w-100" onClick={onClose}>
            Voltar para cliente
          </Button>
        </div>
      </If>
    </Drawer>
  );
}
